import { makeLink } from "./links";
import { urls, contactEmail } from "./urls";
import { get } from "svelte/store";

type stringAnswer = { type: "string"; content: string };
type listAnswer = { type: "list"; content: string[] };
type answer = [stringAnswer, ...(stringAnswer | listAnswer)[]];

export type FAQ = {
  title: string;
  subtitle: string;
  q: string;
  a: string;
  qaList: { question: string; answer: answer }[];
  footnote: string;
}

export const faq = (): FAQ => ({
  title: "LITTLE CAESARS® X CALL OF DUTY® PROMOTION",
  subtitle: "FREQUENTLY ASKED QUESTIONS",
  q: "Q:",
  a: "A:",
  qaList: [
    {
      question: "Experiencing a technical error on the promotion website?",
      answer: [{
        type: "string",
        content: `Please try clearing your cache, refreshing the page or opening a new window in a different browser. If you are still experiencing issues with the promotion, please email us at ${makeLink(get(urls).contactUs, contactEmail)} and include a screenshot, browser/device info and a detailed message regarding your issue.`
      }]
    },
    {
      question: "Who is eligible for this promotion?",
      answer: [{
        type: "string",
        content: `The Little Caesars x Call of Duty Sweepstakes is open only to legal residents of one (1) of the fifty (50) United States or the District of Columbia, Canada (excluding Quebec) and Mexico who are at least eighteen (18) years of age or older as of time/date of entry. You may reference the ${makeLink(get(urls).officialRules, "Official Rules")} for details.`
      }]
    },
    {
      question: "Do I have to register and link my Activision account every time I want to participate?",
      answer: [{
        type: "string",
        content: `You will be prompted to register and link your Activision account with each entry. However, your device may skip the Activision authentication if you already did so within the past 24 hours.`
      }]
    },
    {
      question: "What defines a “Qualifying Purchase” for unlocking in-game rewards?",
      answer: [{
        type: "string",
        content: `It is a restaurant or online order of any food or beverage product from a participating Little Caesars location that meets the following minimum subtotals (before taxes/fees) in the following countries:`
      }, {
        type: "list",
        content: ["United States: $3 USD", "Canada: $4.99 CAD", "Mexico: $50 MXN"]
      }, {
        type: "string",
        content: `Orders from third-party delivery sites and from Kmart, stadium, and Little Caesars Express and concept locations do not qualify for in-game rewards.`
      }, {
        type: "string",
        content: `A “Qualifying Purchase” is not necessary to enter. See the ${makeLink(get(urls).officialRules, "Official Rules")} to learn how to enter without purchase.`
      }]
    },
    {
      question: "How do I enter for in-game rewards and prizes?",
      answer: [{
        type: "string",
        content: `For restaurant Qualifying Purchases in the United States, you will receive a code on the bottom of your printed receipt. Enter this code on this site to unlock the reward or prize. If, for some reason, a code fails to print on the receipt of your restaurant qualifying purchase please contact Little Caesars customer service at ${makeLink(get(urls).contactLittleCaesars, "Contact Us - Little Caesars®")}.`
      }, {
        type: "string",
        content: `For restaurant Qualifying Purchases in Canada and Mexico, you will receive a code on the bottom of your printed receipt. Enter this code on this site to unlock the reward or prize. If, for some reason, a code fails to print on the receipt of your restaurant Qualifying Purchase, you can upload a picture of your receipt on this site instead for verification. The reward or prize will be unlocked once your receipt is verified. Receipt uploads with codes on them will be rejected.`
      }, {
        type: "string",
        content: `For instructions on how to enter without a purchase, see the ${makeLink(get(urls).officialRules, "Official Rules")}.`
      }, {
        type: "string",
        content: `Entrants in Mexico and Canada will be required to correctly answer a skill-based question in order to receive a reward or prize.`
      }]
    },
    {
      question: "What can I win?",
      answer: [{
        type: "string",
        content: `You can unlock one of the following prizes or rewards with each eligible entry to enhance the gaming experience in <i>Call of Duty: Black Ops 7</i>, selected at random upon entry.`
      }, {
        type: "string",
        content: `Prizes (limit 2 total per person):`
      }, {
        type: "list",
        content: [
          "Battle Pass + 15 minutes of Dual 2XP",
          "Call of Duty Points in the following amounts: 500, 1100, 2400 and 13000 (amounts awarded at random) + 15 minutes of Dual 2XP"
        ]
      }, {
        type: "string",
        content: `Rewards:`
      }, {
        type: "list",
        content: [
          "Operator Skin + 30 minutes of Dual 2XP",
          "Emblem + 30 minutes of Dual 2XP",
          "Charm + 30 minutes of Dual 2XP",
          "Calling Card + 30 minutes of Dual 2XP",
          "1 hour of Dual 2XP"
        ]
      }]
    },
    {
      question: "Can I unlock the same item multiple times?",
      answer: [{
        type: "string",
        content: `There is the chance your entry might unlock the same item if you enter multiple times. If you unlock the Operator Skin, Calling Card, Emblem or Charm reward (“Little Caesars rewards”) or the Battle Pass prize more than once, these items will only be applied to your Activision account the first time. If you unlock these items another time, you will receive only the Dual 2XP to level up in the game. Limit of 2 prizes total per person. You may continue to enter once every day to unlock additional rewards even after your prize limit has been reached. Maximum of 40 hours of Dual 2XP total which applies to both prizes and rewards for all Dual 2XP promotions for <i>Call of Duty: Black Ops 7</i>&nbsp; and <i>Call of Duty: Warzone</i>™ (not limited to this promotion).`
      }]
    },
    {
      question: "How many times can I enter per day?",
      answer: [{
        type: "string",
        content: `You may enter up to once per day.`
      }]
    },
    {
      question: "When will I receive my reward or prize?",
      answer: [{
        type: "string",
        content: `Your reward or prize will be fulfilled within 48 hours. Specific details on the reward or prize you won will be sent to your email immediately after entry.`
      }]
    },
    {
      question: "What should I do if I don't see my Battle Pass, Dual 2XP or Little Caesars rewards in-game?",
      answer: [{
        type: "string",
        content: `If you were logged in to the game when you entered your code or when your receipt was verified, please try restarting the game to receive your digital content. If your Battle Pass, Dual 2XP or Little Caesars rewards are still missing, please reach out to ${makeLink(get(urls).activisionSupport, "Activision Support")} for help. Please note the Battle Pass will be delivered in-game at the start of Season 1 for <i>Call of Duty: Black Ops 7</i>, coming later this year`
      }]
    },
    {
      question: "I received a code via email for <i>Call of Duty</i>&nbsp; Points. Where do I redeem it?",
      answer: [{
        type: "string",
        content: `You must log in to your platform's first-party account in order to enter the code:`
      }, {
        type: "list",
        content: [
          `Xbox®: ${makeLink("https://www.xbox.com/redeem")}`,
          `PlayStation®: ${makeLink("https://www.playstation.com/")} (select profile icon)`,
          `Battle.net: ${makeLink("https://us.shop.battle.net/")} (select profile on top-right)`,
          `Steam: ${makeLink("https://www.callofduty.com/redeem")}`,
          `Xbox on PC: ${makeLink("https://www.xbox.com/redeem")}`
        ]
      }, {
        type: "string",
        content: `If you are having issues with your redemption for <i>Call of Duty</i>&nbsp; Points and were logged in to the game when you entered your code, please try restarting the game to receive your digital content.`
      }]
    },
    {
      question: "How long does the promotion last?",
      answer: [{
        type: "string",
        content: `Qualifying Purchases must be made between November 3, 2025, at 12:00:01 am ET - December 31, 2025, at 11:59:59 pm ET, and entered by January 31, 2026, at 11:59:59 pm ET in order to unlock rewards and prizes.`
      }]
    },
    {
      question: "Not seeing your question or issue here?",
      answer: [{
        type: "string",
        content: `Please email ${makeLink(get(urls).contactUs, contactEmail)}.`
      }]
    }
  ],
  footnote: `IN THE EVENT THERE IS A DISCREPANCY OR INCONSISTENCY BETWEEN DISCLOSURES OR OTHER STATEMENTS CONTAINED IN THESE FAQS AND THE TERMS AND CONDITIONS OF THE ${makeLink(get(urls).officialRules, "OFFICIAL RULES")}, THE ${makeLink(get(urls).officialRules, "OFFICIAL RULES")} SHALL PREVAIL, GOVERN AND CONTROL.`
});
